import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { emailCampaignsApi, type EmailCampaign, type RecipientFilter, type RecipientPreview } from '../api';
import { Loader2, ArrowLeft, Play, Pause, X, Send } from 'lucide-react';

const SOURCE_OPTIONS = ['USER_DATABASE', 'FIREBASE_IMPORT', 'CSV_IMPORT', 'MANUAL'];

type FormState = {
  name: string;
  description: string;
  subject: string;
  preheader: string;
  htmlContent: string;
  textContent: string;
  fromEmail: string;
  replyTo: string;
};

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  subject: '',
  preheader: '',
  htmlContent: '',
  textContent: '',
  fromEmail: '',
  replyTo: '',
});

export default function EmailCampaignEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [campaign, setCampaign] = useState<EmailCampaign | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [preview, setPreview] = useState<RecipientPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [confirmingStart, setConfirmingStart] = useState(false);
  const [busy, setBusy] = useState(false);

  const editable = !campaign || ['DRAFT', 'READY'].includes(campaign.status);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        const data = await emailCampaignsApi.findById(Number(id));
        setCampaign(data);
        setForm({
          name: data.name,
          description: data.description || '',
          subject: data.subject,
          preheader: data.preheader || '',
          htmlContent: data.htmlContent,
          textContent: data.textContent || '',
          fromEmail: data.fromEmail,
          replyTo: data.replyTo || '',
        });
        setSelectedSources(data.recipientFilter?.sources || []);
      } catch (error: any) {
        toast({ variant: 'destructive', title: 'Error', description: 'No se pudo cargar la campaña.' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const created = await emailCampaignsApi.create({
          name: form.name,
          description: form.description || undefined,
          subject: form.subject,
          preheader: form.preheader || undefined,
          htmlContent: form.htmlContent,
          textContent: form.textContent || undefined,
          fromEmail: form.fromEmail,
          replyTo: form.replyTo || undefined,
        });
        toast({ title: 'Campaña creada' });
        navigate(`/admin/email-campaigns/${created.id}`);
      } else {
        const updated = await emailCampaignsApi.update(Number(id), {
          name: form.name,
          description: form.description || undefined,
          subject: form.subject,
          preheader: form.preheader || undefined,
          htmlContent: form.htmlContent,
          textContent: form.textContent || undefined,
          fromEmail: form.fromEmail,
          replyTo: form.replyTo || undefined,
        });
        setCampaign(updated);
        toast({ title: 'Campaña actualizada' });
      }
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.response?.data?.error?.message || 'No se pudo guardar la campaña.',
      });
    } finally {
      setSaving(false);
    }
  };

  const buildFilter = (): RecipientFilter => ({
    sources: selectedSources.length > 0 ? selectedSources : undefined,
  });

  const runPreview = async () => {
    if (!campaign) return;
    setPreviewing(true);
    try {
      const result = await emailCampaignsApi.previewRecipients(campaign.id, buildFilter());
      setPreview(result);
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo calcular el preview.' });
    } finally {
      setPreviewing(false);
    }
  };

  const generateSnapshot = async () => {
    if (!campaign) return;
    setBusy(true);
    try {
      const result = await emailCampaignsApi.generateSnapshot(campaign.id, buildFilter());
      toast({ title: `Snapshot generado: ${result.total} destinatarios` });
      const refreshed = await emailCampaignsApi.findById(campaign.id);
      setCampaign(refreshed);
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo generar el snapshot.' });
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (!campaign || !testEmail) return;
    setTesting(true);
    try {
      await emailCampaignsApi.testSend(campaign.id, testEmail);
      toast({ title: 'Email de prueba enviado', description: `Enviado a ${testEmail}` });
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo enviar el email de prueba.' });
    } finally {
      setTesting(false);
    }
  };

  const doStart = async () => {
    if (!campaign) return;
    setBusy(true);
    try {
      const updated = await emailCampaignsApi.start(campaign.id);
      setCampaign(updated);
      toast({ title: 'Campaña iniciada' });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.response?.data?.error?.message || 'No se pudo iniciar la campaña.',
      });
    } finally {
      setBusy(false);
      setConfirmingStart(false);
    }
  };

  const doPause = async () => {
    if (!campaign) return;
    setBusy(true);
    try {
      setCampaign(await emailCampaignsApi.pause(campaign.id));
      toast({ title: 'Campaña pausada' });
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo pausar.' });
    } finally {
      setBusy(false);
    }
  };

  const doResume = async () => {
    if (!campaign) return;
    setBusy(true);
    try {
      setCampaign(await emailCampaignsApi.resume(campaign.id));
      toast({ title: 'Campaña retomada' });
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo retomar.' });
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    if (!campaign) return;
    setBusy(true);
    try {
      setCampaign(await emailCampaignsApi.cancel(campaign.id));
      toast({ title: 'Campaña cancelada' });
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo cancelar.' });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 space-y-6 max-w-3xl">
        <Link to="/admin/email-campaigns">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" /> Volver
          </Button>
        </Link>

        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">{isNew ? 'Nueva campaña' : form.name || 'Editar campaña'}</h1>
          {campaign && <Badge>{campaign.status}</Badge>}
        </div>

        {campaign && campaign.status === 'RUNNING' && (
          <Link to={`/admin/email-campaigns/${campaign.id}/dashboard`}>
            <Button variant="outline" className="w-full">
              Ver dashboard en tiempo real
            </Button>
          </Link>
        )}

        <Card className="p-6 space-y-4">
          <h2 className="text-lg font-semibold">Contenido</h2>
          <div className="space-y-2">
            <Label>Nombre (interno)</Label>
            <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} disabled={!editable} />
          </div>
          <div className="space-y-2">
            <Label>Descripción</Label>
            <Textarea value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} disabled={!editable} />
          </div>
          <div className="space-y-2">
            <Label>Asunto</Label>
            <Input value={form.subject} onChange={(e) => setForm((s) => ({ ...s, subject: e.target.value }))} disabled={!editable} />
          </div>
          <div className="space-y-2">
            <Label>Preheader</Label>
            <Input value={form.preheader} onChange={(e) => setForm((s) => ({ ...s, preheader: e.target.value }))} disabled={!editable} />
          </div>
          <div className="space-y-2">
            <Label>From (email)</Label>
            <Input value={form.fromEmail} onChange={(e) => setForm((s) => ({ ...s, fromEmail: e.target.value }))} disabled={!editable} />
          </div>
          <div className="space-y-2">
            <Label>Reply-To</Label>
            <Input value={form.replyTo} onChange={(e) => setForm((s) => ({ ...s, replyTo: e.target.value }))} disabled={!editable} />
          </div>
          <div className="space-y-2">
            <Label>Contenido HTML</Label>
            <Textarea
              className="font-mono text-xs min-h-[200px]"
              value={form.htmlContent}
              onChange={(e) => setForm((s) => ({ ...s, htmlContent: e.target.value }))}
              disabled={!editable}
            />
          </div>
          <div className="space-y-2">
            <Label>Contenido texto plano (opcional)</Label>
            <Textarea value={form.textContent} onChange={(e) => setForm((s) => ({ ...s, textContent: e.target.value }))} disabled={!editable} />
          </div>
          {editable && (
            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isNew ? 'Crear campaña' : 'Guardar cambios'}
            </Button>
          )}
        </Card>

        {campaign && (
          <>
            <Card className="p-6 space-y-4">
              <h2 className="text-lg font-semibold">Selección de destinatarios</h2>
              <div className="flex flex-wrap gap-2">
                {SOURCE_OPTIONS.map((source) => (
                  <Button
                    key={source}
                    variant={selectedSources.includes(source) ? 'default' : 'outline'}
                    size="sm"
                    disabled={!editable}
                    onClick={() =>
                      setSelectedSources((prev) =>
                        prev.includes(source) ? prev.filter((s) => s !== source) : [...prev, source]
                      )
                    }
                  >
                    {source}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Sin selección = todos los orígenes. Se aplican siempre: activo, no suprimido, no unsubscribed.
              </p>

              <div className="flex gap-2">
                <Button variant="outline" onClick={runPreview} disabled={previewing || !editable}>
                  {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Dry-run (preview)
                </Button>
                <Button onClick={generateSnapshot} disabled={busy || !editable}>
                  Generar snapshot
                </Button>
              </div>

              {preview && (
                <div className="bg-muted/40 rounded-md p-4 text-sm space-y-2">
                  <p>
                    <strong>{preview.eligible}</strong> destinatarios elegibles (ningún email fue enviado en este paso).
                  </p>
                  <p className="text-muted-foreground">
                    Excluidos: {preview.excluded.invalid} inválidos, {preview.excluded.suppressed} suprimidos,{' '}
                    {preview.excluded.unsubscribed} unsubscribed.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(preview.bySource).map(([source, count]) => (
                      <Badge key={source} variant="outline">
                        {source}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {campaign.totalRecipients > 0 && (
                <p className="text-sm">
                  Snapshot actual: <strong>{campaign.totalRecipients}</strong> destinatarios.
                </p>
              )}
            </Card>

            <Card className="p-6 space-y-3">
              <h2 className="text-lg font-semibold">Envío de prueba</h2>
              <div className="flex gap-2">
                <Input placeholder="tu@email.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
                <Button onClick={sendTest} disabled={testing || !testEmail}>
                  {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Enviar prueba
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                El envío de prueba no se registra como destinatario procesado de la campaña.
              </p>
            </Card>

            <Card className="p-6 space-y-3">
              <h2 className="text-lg font-semibold">Control de envío</h2>
              <div className="flex flex-wrap gap-2">
                {campaign.status === 'READY' && (
                  <Button onClick={() => setConfirmingStart(true)} disabled={busy}>
                    <Play className="h-4 w-4 mr-2" /> Iniciar envío
                  </Button>
                )}
                {campaign.status === 'RUNNING' && (
                  <Button variant="outline" onClick={doPause} disabled={busy}>
                    <Pause className="h-4 w-4 mr-2" /> Pausar
                  </Button>
                )}
                {campaign.status === 'PAUSED' && (
                  <Button onClick={doResume} disabled={busy}>
                    <Play className="h-4 w-4 mr-2" /> Retomar
                  </Button>
                )}
                {!['COMPLETED', 'CANCELLED', 'FAILED'].includes(campaign.status) && (
                  <Button variant="destructive" onClick={doCancel} disabled={busy}>
                    <X className="h-4 w-4 mr-2" /> Cancelar
                  </Button>
                )}
              </div>
              {campaign.lastError && <p className="text-sm text-destructive">Último error: {campaign.lastError}</p>}
            </Card>
          </>
        )}

        <AlertDialog open={confirmingStart} onOpenChange={setConfirmingStart}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar inicio de campaña</AlertDialogTitle>
              <AlertDialogDescription>
                Esta campaña enviará {campaign?.totalRecipients ?? 0} mensajes individuales. Los emails ya
                enviados a Amazon SES no podrán cancelarse. ¿Desea iniciar el envío?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={doStart}>Iniciar envío</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
